import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";
import { translate } from "shared/languages";

// Replace a customer's magic login link with a fresh one (the old link stops
// working immediately). Invalidates the login-code query so the profile card
// shows the new link without a reload.
const useRegenerateCustomerLoginLink = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      return client.post(`/v1/customer/${customerId}/login-link/regenerate`);
    },
    onSuccess: (_res, customerId) => {
      queryClient.invalidateQueries({
        queryKey: ["customer", customerId, "login-code"],
      });
      getApiSuccessMessage(
        translate("messages.success.regenerate_login_link")
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Replace a customer's 6-char login code with a fresh one. Codes are stored per
// surface, so this never touches the same person's INSTRUCTOR code if they have
// one — which is what makes rotating a leaked code safe to offer at all.
const useRegenerateCustomerLoginCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      return client.post(`/v1/customer/${customerId}/login-code/regenerate`);
    },
    onSuccess: (_res, customerId) => {
      queryClient.invalidateQueries({
        queryKey: ["customer", customerId, "login-code"],
      });
      getApiSuccessMessage(
        translate("messages.success.regenerate_login_code")
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export { useRegenerateCustomerLoginLink, useRegenerateCustomerLoginCode };
